/**
 * Redux reducer for lists of assets.
 * @module redux/reducers/assets
 */

import update from "immutability-helper";
import { LOGIN_SUCCESS } from "../actions/auth";
import { RECEIVED_CONFIG } from "../actions/app";
import {
  SEARCH_TERMS_CHANGED,
  ASSET_TYPE_CHANGED,
  SORT_BY_CHANGED,
  FILTER_BY_CHANGED,
  SEARCH_TYPE_CHANGED,
  ACTIVE_VIEW_CHANGED,
  CONTEXT_CHANGED,
  CATEGORY_CHANGED,
  CATEGORIES_CHANGED,
  SUBMIT_ASSETS_REQUEST,
  RECEIVED_ASSETS,
  ASSETS_REQUEST_FAILED,
  CLEAR_ASSETS,
  REQUEST_CONTEXTS,
  RECEIVED_CONTEXTS,
  CONTEXTS_REQUEST_FAILED,
  REQUEST_FILTER_OPTIONS,
  RECEIVED_FILTER_OPTIONS,
  FILTER_OPTIONS_REQUEST_FAILED,
  REQUEST_CATEGORIES,
  RECEIVED_CATEGORIES,
  CATEGORIES_REQUEST_FAILED,
  TOGGLE_FAVORITE_SUCCESS,
  PAGE_CHANGED,
  RELOAD,
  TOGGLE_DETAILS_VIEW,
  SET_DEFAULT_CATEGORY,
  SET_DEFAULT_CONTEXT,
  SET_NCS_INFO,
  SET_CATEGORY_GROUPS,
  RESTORE_STATE,
} from "./../actions/assets";
import FilterOption from "../constants/filterOptions";
import SortOption from "../constants/sortOptions";
import { restoreCategories } from "../util/savedStateUtils";

const initialState = {
  searchType: "graphic",
  assetType: "GRAPHIC_TEMPLATE",
  categoryId: -1,
  activeView: 1,
  timeRange: -1,
  contextId: -1,
  pageNumber: 1,
  totalPages: 1,
  searchTerms: "",
  filterBy: FilterOption.NONE,
  sortBy: SortOption.MOST_RECENT,
  maxAssets: 50,
  categoryList: [],
  categories: [],
  categoriesLoading: false,
  categoriesInitialized: false,
  defaultCategory: null,
  contextList: [],
  contextsLoading: false,
  contextsInitialized: false,
  defaultContext: null,
  filterList: [],
  filtersLoading: false,
  filtersInitialized: false,
  ncsInfo: {},
  selectedAssetsList: [],
  assetList: [],
  assetsLoading: false,
  needUpdate: false,
  isTemplate: true,
  isDetailsView: false,
  error: false,
};

const categoryListWithDefaultsSet = (categoryList, ncsInfo) =>
  categoryList.map((group) => {
    let categories = group.categories;
    const selectedCategory = ncsInfo[group.mosTag];

    if (selectedCategory) {
      categories = categories.map((category) => {
        const selected = Array.isArray(selectedCategory)
          ? selectedCategory.some((s) => s === category.name)
          : category.name === selectedCategory;

        return {
          ...category,
          selected,
        };
      });
    }

    return {
      ...group,
      categories,
    };
  });

const ACTION_HANDLERS = {
  [LOGIN_SUCCESS]: (state) => ({
    ...state,
    //needUpdate: true
  }),
  [ASSET_TYPE_CHANGED]: (state, action) => ({
    ...state,
    assetType: action.payload.assetType,
    activeView: action.payload.activeView,
    searchType: action.payload.searchType,
    pageNumber: 1,
    needUpdate: true,
  }),
  [SEARCH_TERMS_CHANGED]: (state, action) => ({
    ...state,
    searchTerms: action.payload.searchTerms,
    pageNumber: 1,
    needUpdate: true,
  }),
  [SORT_BY_CHANGED]: (state, action) => ({
    ...state,
    sortBy: action.payload.sortBy,
    needUpdate: true,
  }),
  [FILTER_BY_CHANGED]: (state, action) => ({
    ...state,
    filterBy: action.payload.filterBy,
    pageNumber: 1,
    needUpdate: true,
  }),
  [SEARCH_TYPE_CHANGED]: (state, action) => ({
    ...state,
    searchType: action.payload.searchType,
    pageNumber: 1,
    needUpdate: true,
  }),
  [ACTIVE_VIEW_CHANGED]: (state, action) => ({
    ...state,
    activeView: action.payload.activeView,
    pageNumber: 1,
    needUpdate: true,
  }),
  [CONTEXT_CHANGED]: (state, action) => ({
    ...state,
    contextId: !isNaN(action.payload.contextId)
      ? parseInt(action.payload.contextId, 10)
      : -1,
    pageNumber: 1,
    needUpdate: true,
  }),
  [CATEGORY_CHANGED]: (state, action) => {
    const index = state.categoryList.findIndex(
      (x) => x.id === action.payload.groupId
    );
    if (index >= 0) {
      return update(state, {
        categoryList: {
          [index]: {
            categories: {
              $set: state.categoryList[index].categories.map((category) => ({
                ...category,
                selected:
                  category.id === parseInt(action.payload.categoryId, 10),
              })),
            },
          },
        },
        pageNumber: { $set: 1 },
        needUpdate: { $set: true },
      });
    }
    return {
      ...state,
      pageNumber: 1,
      needUpdate: true,
    };
  },
  [CATEGORIES_CHANGED]: (state, action) => {
    const index = state.categoryList.findIndex(
      (x) => x.id === action.payload.groupId
    );
    if (index >= 0) {
      return update(state, {
        categoryList: {
          [index]: {
            categories: {
              $set: state.categoryList[index].categories.map((category) => ({
                ...category,
                selected: action.payload.categoryIds.some(
                  (id) => id === category.id
                ),
              })),
            },
          },
        },
        pageNumber: { $set: 1 },
        needUpdate: { $set: true },
      });
    }
    return {
      ...state,
      pageNumber: 1,
      needUpdate: true,
    };
  },
  [SUBMIT_ASSETS_REQUEST]: (state) => ({
    ...state,
    assetsLoading: true,
    needUpdate: false,
  }),
  [RECEIVED_ASSETS]: (state, action) => ({
    ...state,
    assetList: action.payload.assetList,
    pageNumber: action.payload.pageNumber,
    totalPages: action.payload.totalPages,
    assetsLoading: false,
    error: false,
  }),
  [ASSETS_REQUEST_FAILED]: (state) => ({
    // Todo: Maybe assign an error here
    ...state,
    error: true,
    assetsLoading: false,
    needUpdate: false,
  }),
  [CLEAR_ASSETS]: (state) => ({
    ...state,
    assetList: [],
    pageNumber: 1,
    totalPages: 1,
    needUpdate: false,
  }),
  [REQUEST_CONTEXTS]: (state) => ({
    ...state,
    contextsLoading: true,
  }),
  [RECEIVED_CONTEXTS]: (state, action) => ({
    ...state,
    contextsLoading: false,
    contextList: action.payload.contextList,
    contextsInitialized: true,
  }),
  [CONTEXTS_REQUEST_FAILED]: (state) => ({
    ...state,
    contextsLoading: false,
  }),
  [REQUEST_FILTER_OPTIONS]: (state) => ({
    ...state,
    filtersLoading: true,
  }),
  [RECEIVED_FILTER_OPTIONS]: (state, action) => ({
    ...state,
    filtersLoading: false,
    filterList: action.payload.filterList,
    filtersInitialized: true,
  }),
  [FILTER_OPTIONS_REQUEST_FAILED]: (state) => ({
    ...state,
    filtersLoading: false,
  }),
  [REQUEST_CATEGORIES]: (state) => ({
    ...state,
    assetList: [],
    pageNumber: 1,
    totalPages: 1,
    categoriesLoading: true,
  }),
  [RECEIVED_CATEGORIES]: (state, action) => {
    let categoryList = action.payload.categoryList.map((group) => ({
      ...group,
      categories: group.categories.map((category) => ({
        ...category,
        selected: false,
      })),
    }));

    if (
      (!state.categoryList || state.categoryList.length === 0) &&
      state.ncsInfo
    ) {
      categoryList = categoryListWithDefaultsSet(categoryList, state.ncsInfo);
    }

    return {
      ...state,
      categoriesLoading: false,
      categoryList,
      categoriesInitialized: true,
    };
  },
  [CATEGORIES_REQUEST_FAILED]: (state) => ({
    ...state,
    categoriesLoading: false,
    categoryList: null,
  }),
  [TOGGLE_FAVORITE_SUCCESS]: (state, action) => {
    const index = state.assetList.findIndex((a) => a.id === action.payload.id);
    if (index >= 0) {
      return update(state, {
        assetList: {
          [index]: {
            isFavorite: { $set: action.payload.favorite },
          },
        },
      });
    }
    return state;
  },
  [PAGE_CHANGED]: (state, action) => ({
    ...state,
    pageNumber: action.payload.page,
    needUpdate: true,
  }),
  [RELOAD]: (state) => ({
    ...state,
    needUpdate: true,
  }),
  [RECEIVED_CONFIG]: (state, action) => ({
    ...state,
    maxAssets:
      action.payload.config.luci &&
      action.payload.config.luci.maxAssetsDisplayed
        ? action.payload.config.luci.maxAssetsDisplayed
        : 50,
  }),
  [TOGGLE_DETAILS_VIEW]: (state) => ({
    ...state,
    isDetailsView: !state.isDetailsView,
  }),
  [SET_DEFAULT_CATEGORY]: (state, action) => ({
    ...state,
    defaultCategory: action.payload.category,
  }),
  [SET_DEFAULT_CONTEXT]: (state, action) => ({
    ...state,
    defaultContext: action.payload.context,
  }),
  [SET_NCS_INFO]: (state, action) => {
    const { ncsInfo } = action.payload;
    let categoryList = state.categoryList;
    let contextId = state.contextId;
    let needUpdate = state.needUpdate;

    if (categoryList && categoryList.length > 0) {
      categoryList = categoryListWithDefaultsSet(categoryList, ncsInfo);
      needUpdate = true;
    }

    if (state.contextList && state.contextList.length > 0 && ncsInfo.showType) {
      const selectedContext = state.contextList.find(
        (c) => c.name === ncsInfo.showType
      );
      if (selectedContext) {
        contextId = selectedContext.id;
        needUpdate = true;
      }
    }

    return {
      ...state,
      categoryList,
      contextId,
      ncsInfo,
      needUpdate,
    };
  },
  [SET_CATEGORY_GROUPS]: (state, action) => ({
    ...state,
    categories: action.payload.categories,
    categoriesLoading: false,
  }),
  [RESTORE_STATE]: (state, action) => ({
    ...state,
    contextId: action.payload.contextId || state.contextId,
    categoryList: restoreCategories(
      state.categoryList,
      action.payload.categoryList
    ),
    filterBy: action.payload.filterBy || state.filterBy,
    assetType: action.payload.assetType || state.assetType,
    activeView: action.payload.activeView || state.activeView,
    timeRange: action.payload.timeRange || state.timeRange,
    searchType: action.payload.searchType || state.searchType,
    needUpdate: true,
  }),
};

export default function assets(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type];

  return handler ? handler(state, action) : state;
}



// WEBPACK FOOTER //
// ./app/reducers/assets.js